/**
 * Entity classes for the platformer runtime — barrel module.
 *
 * The classes used to live here in one 900-line monolith; they are
 * now split by category and re-exported from this path so existing
 * imports keep working:
 *
 *   PlayerEntity.js     — the player (input, power state, physics)
 *   EnemyEntities.js    — WalkerEnemyBase, Goomba, Koopa
 *   ItemEntities.js     — Coin, Mushroom, FlagGoal
 *   TriggerEntities.js  — AreaTrigger, CameraZone, PointLight, ParticleEmitter
 *   entityHelpers.js    — applyCollisionConfig, spawnSprite, misc utils
 *
 * Each class follows the same minimal contract:
 *
 *   constructor(c, r, board, theme, opts?)  — places the entity at
 *       its tile-cell spawn point. Reads its sprite from SkinResolver
 *       via the slot in PLATFORMER_ENTITY_DEFS, falling back to a
 *       procedural Pixi shape when no PNG is set.
 *
 *   update(controls?, board?)               — per-frame tick. Mutates
 *       position, runs physics, applies AI. The Player accepts a
 *       `controls` snapshot (left/right/jumpHeld/jumpPressed/run) so
 *       PlatformerScene drives input from one place.
 *
 *   onCollidePlayer(player) → 'damage' | 'stomp' | 'pickup' | 'win'
 *                          | null              — invoked by the scene
 *       when the player overlaps this entity. Centralising the
 *       reaction here keeps PlatformerScene's update loop short.
 *
 * Every entity exposes a `display: Container` so the scene can
 * `addChild(entity.display)` without caring whether the visual is a
 * sprite, a graphics fallback, or a composed group.
 */

import { aabbOverlap, isGrounded } from './PlatformerPhysics.js';
import { applyCollisionConfig } from './entityHelpers.js';
import { PlayerEntity } from './PlayerEntity.js';
import { WalkerEnemyBase, GoombaEntity, KoopaEntity } from './EnemyEntities.js';
import { CoinEntity, MushroomEntity, FlagGoalEntity, CheckpointEntity } from './ItemEntities.js';
import {
    AreaTriggerEntity,
    CameraZoneEntity,
    PointLightEntity,
    ParticleEmitterEntity,
} from './TriggerEntities.js';
import { MovingPlatformEntity, carryRiders, isRidingPlatform } from './PlatformEntities.js';
import { NoteEntity } from './PlatformerNoteEntity.js';

export { PlayerEntity };
export { WalkerEnemyBase, GoombaEntity, KoopaEntity };
export { CoinEntity, MushroomEntity, FlagGoalEntity, CheckpointEntity };
export { AreaTriggerEntity, CameraZoneEntity, PointLightEntity, ParticleEmitterEntity };
export { MovingPlatformEntity, carryRiders, isRidingPlatform };
export { applyCollisionConfig };

// ── Factory ───────────────────────────────────────────────────────

/**
 * Build an entity instance from a stage JSON entity record. The
 * scene calls this once per entity during spawn, then drops the
 * result's `display` into the world container and adds the body to
 * its update list.
 *
 * Returns null when the entity type isn't supported by the runtime
 * so the scene can skip it gracefully.
 */
export function spawnEntity(record, board, theme, opts = {}) {
    const { type, c, r, props } = record;
    let inst = null;
    switch (type) {
        case 'player.spawn': {
            const power = props?.startPower === 'super' ? 'super' : 'small';
            inst = new PlayerEntity(c, r, board, theme, { power, props: props || {} });
            break;
        }
        case 'goomba':   inst = new GoombaEntity(c, r, board, theme, props || {}); break;
        case 'koopa':    inst = new KoopaEntity(c, r, board, theme, props || {}); break;
        case 'coin':     inst = new CoinEntity(c, r, board, theme); break;
        case 'mushroom': inst = new MushroomEntity(c, r, board, theme, props || {}); break;
        case 'flag.goal':inst = new FlagGoalEntity(c, r, board, theme); break;
        case 'area.trigger': inst = new AreaTriggerEntity(c, r, board, theme, props || {}); break;
        case 'camera.zone': inst = new CameraZoneEntity(c, r, board, theme, props || {}); break;
        case 'light.point': inst = new PointLightEntity(c, r, board, theme, props || {}); break;
        case 'fx.emitter': inst = new ParticleEmitterEntity(c, r, board, theme, props || {}); break;
        case 'platform.moving': inst = new MovingPlatformEntity(c, r, board, theme, props || {}); break;
        case 'note':       inst = new NoteEntity(c, r, board, theme, props || {}); break;
        case 'checkpoint': inst = new CheckpointEntity(c, r, board, theme); break;
        default:
            return null;
    }
    return applyCollisionConfig(inst, record);
}

// Re-export AABB helpers for the scene's collision sweep so it
// doesn't have to import PlatformerPhysics directly.
export { aabbOverlap, isGrounded };
